package com.sddamico.draftmachine.ds.motion

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.sddamico.draftmachine.ds.DmMotion
import kotlinx.coroutines.delay

/**
 * True inside a [Presence] whose content is on its way out. Overlays read it to play their exit
 * animation and stop taking input.
 */
val LocalExiting = compositionLocalOf { false }

/**
 * Keeps [content] on screen for [exitMillis] after [target] goes null, so it can animate out instead
 * of vanishing. While leaving, [content] is rendered with the last non-null value and
 * [LocalExiting] is true.
 *
 * Wrap conditional overlays at the call site — `Presence(state.editingPick) { pick -> ... }` in place
 * of `state.editingPick?.let { pick -> ... }`.
 */
@Composable
fun <T : Any> Presence(
  target: T?,
  exitMillis: Int = DmMotion.overlayExitMs,
  content: @Composable (T) -> Unit,
) {
  val last = remember { arrayOfNulls<Any>(1) }
  if (target != null) last[0] = target
  var gone by remember(target == null) { mutableStateOf(target == null && last[0] == null) }
  LaunchedEffect(target == null) {
    if (target == null && last[0] != null) {
      delay(exitMillis.toLong())
      last[0] = null
      gone = true
    }
  }
  @Suppress("UNCHECKED_CAST")
  val shown = (target ?: last[0]) as T?
  if (gone || shown == null) return
  val exiting = target == null || LocalExiting.current
  CompositionLocalProvider(LocalExiting provides exiting) { content(shown) }
}

/** [Presence] for overlays driven by a flag rather than a value. */
@Composable
fun Presence(
  visible: Boolean,
  exitMillis: Int = DmMotion.overlayExitMs,
  content: @Composable () -> Unit,
) {
  Presence(if (visible) Unit else null, exitMillis) { content() }
}
